import React, { useState, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * GuardedDialog — Dialog protégé contre la perte de données
 * 
 * Remplace <Dialog> + <DialogContent> pour les formulaires critiques.
 * 
 * Props:
 * @param {boolean} open — état d'ouverture du Dialog
 * @param {function} onOpenChange — callback de changement d'état
 * @param {boolean} isDirty — true si le formulaire contient des modifications non sauvées
 * @param {string} className — classes CSS pour le DialogContent
 * @param {React.ReactNode} children — contenu du Dialog
 * 
 * Comportement:
 * - isDirty=false → fermeture immédiate (overlay, Escape, X)
 * - isDirty=true → toute tentative de fermeture affiche AlertDialog de confirmation
 */
const GuardedDialog = ({ open, onOpenChange, isDirty = false, className, children }) => {
  const [showConfirm, setShowConfirm] = useState(false);

  // Tentative de fermeture — intercepte si dirty
  const handleRequestClose = useCallback((wantOpen) => {
    // Ouverture → toujours autorisé
    if (wantOpen) {
      onOpenChange(true);
      return;
    }
    // Fermeture avec modifications → confirmation
    if (isDirty) {
      setShowConfirm(true);
      return;
    }
    // Fermeture sans modifications → directe
    onOpenChange(false);
  }, [isDirty, onOpenChange]);

  // Confirmation: quitter sans enregistrer
  const handleConfirmClose = useCallback(() => {
    setShowConfirm(false);
    onOpenChange(false);
  }, [onOpenChange]);

  // Annulation: continuer la saisie
  const handleCancelClose = useCallback(() => {
    setShowConfirm(false);
  }, []);

  // Bloquer clic overlay si dirty
  const handleInteractOutside = useCallback((e) => {
    if (isDirty) {
      e.preventDefault();
      setShowConfirm(true);
    }
  }, [isDirty]);

  // Bloquer Escape si dirty
  const handleEscapeKeyDown = useCallback((e) => {
    if (isDirty) {
      e.preventDefault();
      setShowConfirm(true);
    }
  }, [isDirty]);

  return (
    <>
      <DialogPrimitive.Root open={open} onOpenChange={handleRequestClose}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
          <DialogPrimitive.Content
            className={cn(
              "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
              className
            )}
            onInteractOutside={handleInteractOutside}
            onEscapeKeyDown={handleEscapeKeyDown}
            aria-describedby={undefined}
          >
            {children}
            <DialogPrimitive.Close
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
              onClick={(e) => {
                if (isDirty) {
                  e.preventDefault();
                  setShowConfirm(true);
                }
              }}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Fermer</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>

      {/* AlertDialog de confirmation */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent data-testid="unsaved-changes-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Quitter sans enregistrer ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les modifications non enregistrées seront perdues.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose} data-testid="unsaved-changes-cancel">
              Continuer la saisie
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmClose}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="unsaved-changes-confirm"
            >
              Quitter sans enregistrer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default GuardedDialog;
